/**
 * Remember a returning customer's own details ON THEIR OWN DEVICE.
 *
 * Deliberately device-local and deliberately NOT silent. The shared-device
 * problem is the whole risk here: a family laptop must never quietly submit
 * one person's contact details under another person's order. So the renderer
 * always shows what it restored on a confirm screen before anything is sent,
 * and offers a one-tap "Not you?" that wipes this store.
 *
 * Only fields the definition names in `remember` are ever written, which keeps
 * order contents (services, quantities, notes) out of storage entirely — they
 * differ every order and are nobody's business but the current one's.
 *
 * Storage is keyed per form slug, so the in-store kiosk form (a different
 * slug, with no `remember` block) can never pick up a previous walk-in's
 * details from the shared iPad.
 */
import type { Answers, FormDefinition } from './types';

/** Bump to invalidate every stored record (schema change, privacy reset). */
const VERSION = 'v1';

const keyFor = (slug: string) => `rplf:${VERSION}:${slug}:details`;

/** Every field id the definition allows us to remember. */
export function rememberedIds(def: FormDefinition): string[] {
  const r = def.remember;
  if (!r) return [];
  return [...(r.review ?? []), ...(r.fill ?? [])];
}

/**
 * Restore previously saved answers. Returns {} when nothing is stored, the
 * definition doesn't opt in, or storage is unavailable (Safari private mode
 * throws on access, and a browser refusing us storage is not an error worth
 * showing a customer).
 */
export function loadDetails(def: FormDefinition): Record<string, string> {
  const allowed = new Set(rememberedIds(def));
  if (allowed.size === 0) return {};
  try {
    const raw = window.localStorage.getItem(keyFor(def.slug));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, string> = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      // Re-filter on read, not just on write: a definition that stops
      // remembering a field must stop restoring it immediately, without
      // waiting for the customer's storage to be rewritten.
      if (allowed.has(id) && typeof value === 'string' && value.trim() !== '') {
        out[id] = value;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Persist the remembered subset of a submitted order. Never throws. */
export function saveDetails(def: FormDefinition, answers: Answers): void {
  const allowed = rememberedIds(def);
  if (allowed.length === 0) return;
  try {
    const out: Record<string, string> = {};
    for (const id of allowed) {
      const value = answers[id];
      // Single-value fields only: the remembered set is identity and address,
      // and a stored array would restore as the wrong shape.
      if (typeof value === 'string' && value.trim() !== '') out[id] = value.trim();
    }
    if (Object.keys(out).length === 0) return;
    window.localStorage.setItem(keyFor(def.slug), JSON.stringify(out));
  } catch {
    // Quota, private mode, or storage disabled: remembering is a convenience,
    // never a reason to disturb a customer who has just ordered successfully.
  }
}

/** Forget this device's saved details ("Not you?"). Never throws. */
export function clearDetails(def: FormDefinition): void {
  try {
    window.localStorage.removeItem(keyFor(def.slug));
  } catch {
    /* nothing we can do, and nothing the customer needs to hear */
  }
}
